import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/Button';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { SvgArrow } from 'components/Svgs';
import When from 'components/When';
import { ICONS, NAV_ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import './ItemInReviewModal.scss';

const { block, element } = bem('item-in-review-modal');

const messages = defineMessages({
  title: {
    id: 'items.item-in-review-modal.title',
    defaultMessage: 'Your item is in review',
  },
  pendingReview: {
    id: 'items.item-in-review-modal.pending-review',
    defaultMessage: 'Pending review',
  },
  whatToExpect: {
    id: 'items.item-in-review-modal.what-to-expect',
    defaultMessage: 'Here’s what to expect:',
  },
  teamIsReviewing: {
    id: 'items.item-in-review-modal.team-is-reviewing',
    defaultMessage: `<strong>Our team is reviewing your item</strong> to ensure your pricing information is correct.`,
  },
  customersCannotSee: {
    id: 'items.item-in-review-modal.customers-cannot-see',
    defaultMessage: `<strong>Customers can’t see your item</strong> while it’s in review.`,
  },
  expectedTimeInReview: {
    id: 'items.item-in-review-modal.expected-time-in-review',
    defaultMessage: 'Our review process typically takes <strong>3-5 business days</strong>.',
  },
  receiveAnEmail: {
    id: 'items.item-in-review-modal.receive-an-email',
    defaultMessage: '<strong>You’ll receive an email</strong> when your item is approved and available for purchase.',
  },
  close: {
    id: 'items.item-in-review-modal.button.close',
    defaultMessage: 'Close',
  },
});

export const ITEM_IN_REVIEW_MODAL_NAME = 'ItemInReviewModal';

function ItemInReviewModal({ itemName }) {
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);

  function handleClose() {
    dispatch(closeModal(ITEM_IN_REVIEW_MODAL_NAME));
  }

  return (
    <div className={block()}>
      <header className={element('header')}>
        <h3 className={element('title')}>
          <Message message={messages.title} />
        </h3>
        <Isvg src={ICONS.cross} className={element('close-icon')} onClick={handleClose} />
      </header>
      <section className={element('content')}>
        <div className={element('banner')}>
          <div className={element('banner-content-container')}>
            <span className={element('item-name')}>{itemName}</span>
            <div className={element('faux-dropdown')}>
              <span className={element('status-wrapper')}>
                <Isvg src={ICONS.clock} className={element('status-marker')} />
                <When predicate={!isMobile}>
                  <Message message={messages.pendingReview} />
                </When>
              </span>
              <SvgArrow direction="down" theme="disabled" />
            </div>
          </div>
        </div>
        <Message message={messages.whatToExpect} />
        <div className={element('bullet-container')}>
          <span className={element('bullet-point')}>
            <Isvg src={ICONS.searchGlass} className={element('icon')} />
            <Message
              message={messages.teamIsReviewing}
              values={{
                strong: text => <strong>{text}</strong>,
              }}
            />
          </span>
          <span className={element('bullet-point')}>
            <Isvg src={ICONS.eyeWithCrossDark} className={element('icon')} />
            <Message
              message={messages.customersCannotSee}
              values={{
                strong: text => <strong>{text}</strong>,
              }}
            />
          </span>
          <span className={element('bullet-point')}>
            <Isvg src={ICONS.clock} className={element('icon', 'clock-icon')} />
            <Message
              message={messages.expectedTimeInReview}
              values={{
                strong: text => <strong>{text}</strong>,
              }}
            />
          </span>
          <span className={element('bullet-point')}>
            <Isvg src={NAV_ICONS.appointments} className={element('icon')} />
            <Message
              message={messages.receiveAnEmail}
              values={{
                strong: text => <strong>{text}</strong>,
              }}
            />
          </span>
        </div>
      </section>
      <footer className={element('footer')}>
        <Message
          as={Button}
          className={element('close-btn')}
          message={messages.close}
          onClick={handleClose}
          styled="primary"
          type={Button.TYPES.BUTTON}
        />
      </footer>
    </div>
  );
}

ItemInReviewModal.propTypes = {
  itemName: PropTypes.string,
};

export default ItemInReviewModal;
